import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Send, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { CONTACT_EMAIL } from "@/data/site";

const AUDIENCES = ["Investors", "Partners", "Customers", "Talent"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setSent(true);
    } catch {
      setError("Something went wrong. Please email us directly at " + CONTACT_EMAIL + ".");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder-white/35 outline-none focus:border-cyan-300/40 transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 sm:py-40">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="glass relative overflow-hidden rounded-[2rem] px-6 py-16 sm:px-16 sm:py-20">
            <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-[300px] w-[300px] glow-violet" />
            <div className="absolute inset-0 bg-grid opacity-40" />

            <div className="relative grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-cyan-300/80">07 — Get in Touch</span>
                <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-500 tracking-[-0.02em] text-white leading-[1.12]">
                  Let's build the future of intelligent software, together.
                </h2>
                <p className="mt-6 max-w-xl text-base text-white/55 font-light">
                  We'd love to hear from investors, partners, customers, and people who want to help build what's next at ArcaCentum.
                </p>
                <div className="mt-8 flex flex-wrap gap-2.5">
                  {AUDIENCES.map((a) => (
                    <span key={a} className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs text-white/60">{a}</span>
                  ))}
                </div>
                <div className="mt-8 space-y-2.5">
                  <a href="mailto:admin@arcacentum.com" data-testid="contact-email-link"
                    className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                    <Mail size={15} className="text-cyan-300" /> admin@arcacentum.com
                  </a>
                  <a href="tel:+18889775190" data-testid="contact-phone-link"
                    className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                    <Phone size={15} className="text-cyan-300" /> (888) 977-5190
                  </a>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
                {sent ? (
                  <div className="text-center py-8" data-testid="contact-success">
                    <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
                    <h3 className="font-display text-xl text-white mt-4">Message sent</h3>
                    <p className="text-white/55 text-sm mt-2">Thanks for reaching out — our team will be in touch shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-3.5" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-3.5">
                      <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="contact-name" />
                      <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} data-testid="contact-email" />
                    </div>
                    <input placeholder="Subject (optional)" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={field} data-testid="contact-subject" />
                    <textarea required rows={4} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={field + " resize-none"} data-testid="contact-message" />
                    {error && <p className="text-sm text-red-300" data-testid="contact-error">{error}</p>}
                    <button type="submit" disabled={loading} data-testid="contact-submit"
                      className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-white px-8 py-3.5 text-sm font-500 text-[#0a0a0f] disabled:opacity-60">
                      {loading ? "Sending…" : <>Send message <Send size={16} /></>}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
